import { PropsWithChildren, useEffect, useState } from 'react';

import {
  Link,
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  sidebarConfig,
  useSidebarOpenState,
} from '@backstage/core-components';
import {
  configApiRef,
  errorApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { usePermission } from '@backstage/plugin-permission-react';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { Administration } from '@backstage-community/plugin-rbac';
import { customMetricsInsightsReadPermission } from '@internal/backstage-plugin-custom-metrics';
import {
  feedbackListPermission,
  npsReadPermission,
} from '@internal/backstage-plugin-feedback-common';
import { CircularProgress } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import AssessmentIcon from '@material-ui/icons/Assessment';
import CategoryIcon from '@material-ui/icons/Category';
import FeedbackIcon from '@material-ui/icons/Feedback';
import HomeIcon from '@material-ui/icons/Home';
import MenuIcon from '@material-ui/icons/Menu';
import GroupIcon from '@material-ui/icons/People';
import SearchIcon from '@material-ui/icons/Search';
import TextsmsOutlined from '@material-ui/icons/TextsmsOutlined';

// Use public assets to avoid importing images in tests/build
const MyCustomLogoFullLight = '/carrier_logo_300.png';
const MyCustomLogoFullDark = '/carrier_logo_dark_300.png';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
    height: sidebarConfig.logoHeight,
    '& img': {
      width: 'auto',
      height: '120%',
      transform: 'scale(1.5)',
      transformOrigin: 'left',
    },
  },
});

const LogoFullImg = ({ isDarkTheme }: { isDarkTheme: boolean }) => (
  <img
    alt="Login button"
    src={isDarkTheme ? MyCustomLogoFullDark : MyCustomLogoFullLight}
  />
);

const LogoIcon = ({ isDarkTheme }: { isDarkTheme: boolean }) => (
  <img
    alt="Login icon"
    src={isDarkTheme ? MyCustomLogoFullDark : MyCustomLogoFullLight}
    style={{
      width: sidebarConfig.iconSize,
      objectFit: 'contain',
      height: sidebarConfig.iconSize,
      transform: 'scale(2.5) translateX(-7px)',
      alignItems: 'center',
      flexFlow: 'row nowrap',
      marginBottom: -14,
    }}
  />
);

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();
  const theme = useTheme();
  const isDarkTheme = theme.palette.type === 'dark';

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? (
          <LogoFullImg isDarkTheme={isDarkTheme} />
        ) : (
          <LogoIcon isDarkTheme={isDarkTheme} />
        )}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const config = useApi(configApiRef);
  const errorApi = useApi(errorApiRef);
  const identityApi = useApi(identityApiRef);
  const [identityLoading, setIdentityLoading] = useState(true);
  const [showHomeDashboard, setShowHomeDashboard] = useState(false);

  const { allowed: canViewFeedback, loading: feedbackLoading } = usePermission({
    permission: feedbackListPermission,
  });

  const { allowed: canViewNps, loading: npsLoading } = usePermission({
    permission: npsReadPermission,
  });

  const { allowed: canViewInsights, loading: insightsLoading } = usePermission({
    permission: customMetricsInsightsReadPermission,
  });

  // The page is considered loading while we fetch:
  // 1. The user's identity is being fetched
  // 2. Permission to the feedback results is being determined
  // 3. Permission to the NPS score is being determined
  // 4. Permission to Insights (custom metrics) is being determined
  const isLoading =
    identityLoading || feedbackLoading || npsLoading || insightsLoading;

  useEffect(() => {
    setShowHomeDashboard(
      config.getOptionalBoolean('configFeatureFlags.showHomeDashboard') ??
        false,
    );

    let isMounted = true;

    const handleError = (error: unknown) => {
      // Use better stringification for non-Error objects
      if (error instanceof Error) {
        errorApi.post(error);
      } else if (typeof error === 'object' && error !== null) {
        errorApi.post(new Error(JSON.stringify(error)));
      } else {
        errorApi.post(new Error(String(error)));
      }
    };

    const checkIdentity = async () => {
      try {
        await identityApi.getCredentials();
      } catch (error: unknown) {
        handleError(error);
      } finally {
        if (isMounted) {
          setIdentityLoading(false);
        }
      }
    };

    checkIdentity();

    return () => {
      isMounted = false;
    };
  }, [errorApi, identityApi, config]);

  if (isLoading) {
    return (
      <div
        data-testid="progress"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {showHomeDashboard && (
            <>
              <SidebarItem icon={HomeIcon} to="/" text="Home" />
              <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
            </>
          )}
          {!showHomeDashboard && (
            <SidebarItem icon={HomeIcon} to="catalog" text="Catalog" />
          )}
          <Administration />
          {canViewInsights && (
            <SidebarItem icon={AssessmentIcon} to="insights" text="Insights" />
          )}
          <MyGroupsSidebarItem
            singularTitle="My Group"
            pluralTitle="My Groups"
            icon={GroupIcon}
          />
          <SidebarItem
            icon={CreateComponentIcon}
            to="create"
            text="Create..."
          />
          {canViewFeedback &&
            <SidebarItem
              icon={TextsmsOutlined}
              to="feedback"
              text="View Feedback"
            />
          }
          {canViewNps && <SidebarItem icon={FeedbackIcon} to="nps" text="NPS" />}
          <SidebarDivider />
          <SidebarScrollWrapper />
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
